import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { Subject } from 'rxjs';
import {
  IFacetedFilterDataItem,
  IHierarchyValue,
  IReplayItem,
  ISelectedHierarchyInfo,
  ISelectionInfo,
} from '../../../../types/faceted-filter.types';
import { TypeCheck } from '../../../../Utils/type-check';
import { TypeInstanceOf } from '../../../../Utils/type-instance-of';
import { ContinuationIndicatorComponent } from '../../../faceted-filter/continuation-indicator/continuation-indicator.component';

@Component({
  selector: 'app-hierarchy-pane',
  templateUrl: './hierarchy-pane.component.html',
  styleUrls: ['./hierarchy-pane.component.scss'],
  standalone: true,
  imports: [FormsModule, MatCheckboxModule, MatListModule, ContinuationIndicatorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HierarchyPaneComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<boolean>();

  canSelectAll = false;
  canSelectChildren = true;

  selectedHierarchyContainerItems: IFacetedFilterDataItem[] = [];
  selectedHierarchyFacetItems: IFacetedFilterDataItem[] = [];

  hierarchyContainerItemsList: IFacetedFilterDataItem[] = [];
  hierarchyFacetItemsList: IFacetedFilterDataItem[] = [];

  isIndeterminate = false;
  isSelectAllChecked = false;
  selectedContainerMap: Record<number, boolean> = {};

  @Input() set replayItem(replayItem: IReplayItem) {
    this.item = { ...replayItem.item };
    this.handleReplayItem(replayItem);
  }

  // TODO: Simplify this \/\/\/\/
  @Input() set replayValue(value: IHierarchyValue[] | unknown) {
    if (Array.isArray(value) && TypeInstanceOf.HierarchyValue(value[0])) {
      const selectedItems: IFacetedFilterDataItem[] = [];
      for (let i = 0; i < value.length; i++) {
        const val: IHierarchyValue = value[i];
        const selected = this.item.items?.find((item) => (item.value as IHierarchyValue).value === val.value);
        if (selected) selectedItems.push(selected);
      }
      if (selectedItems?.length) {
        const firstItem = selectedItems[0];
        if (firstItem && TypeCheck.isHierarchyContainer(firstItem.type)) {
          this.selectedHierarchyContainerItems = [...selectedItems];
          this.rebuildContainerMap();
          const selection: ISelectedHierarchyInfo = this.getSelectionInfo(this.selectedHierarchyContainerItems);
          this.onHierarchyReplaySelection.emit(selection);
        } else {
          this.selectedHierarchyFacetItems = [...selectedItems];
          this.updateCheckboxState();
          const selection: ISelectedHierarchyInfo = this.getSelectionInfo(this.selectedHierarchyFacetItems);
          this.onHierarchyReplaySelection.emit(selection);
        }
      }
    }
  }

  @Input() item!: IFacetedFilterDataItem;
  @Output() onHierarchyContainerItemsChanged = new EventEmitter<ISelectionInfo>();
  @Output() onHierarchyItemsChanged = new EventEmitter<ISelectionInfo>();
  @Output() onHierarchyReplaySelection = new EventEmitter<ISelectionInfo>();

  ngOnInit(): void {
    if (this.item) {
      this.buildHierarchyContainerItemsList();
      this.buildHierarchyFacetItemsList();
      this.setSelectionMode();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private updateCheckboxState(): void {
    const selectedCount = this.selectedHierarchyFacetItems?.length ?? 0;
    const totalCount = this.hierarchyFacetItemsList.length;
    this.isSelectAllChecked = selectedCount > 0 && selectedCount === totalCount;
    this.isIndeterminate = selectedCount >= 1 && selectedCount !== totalCount;
  }

  private rebuildContainerMap(): void {
    this.selectedContainerMap = {};
    for (let i = 0; i < this.hierarchyContainerItemsList.length; i++) {
      this.selectedContainerMap[i] = this.selectedHierarchyContainerItems.includes(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- safe to assume item exists
        this.hierarchyContainerItemsList[i]!
      );
    }
  }

  selectAllCheckboxChanged($event: { checked: boolean }): void {
    this.selectedHierarchyFacetItems = [];
    if ($event.checked) {
      this.selectedHierarchyFacetItems = [...this.hierarchyFacetItemsList];
    }
    this.updateCheckboxState();
    const selection: ISelectedHierarchyInfo = {
      hierarchies: this.mapValues(this.selectedHierarchyFacetItems),
      item: this.item,
    };
    this.onHierarchyItemsChanged.emit(selection);
  }

  hierarchyContainerItemsChanged($event: MatSelectionListChange): void {
    const lastSelectedOption: IFacetedFilterDataItem | undefined =
      this.selectedHierarchyContainerItems.length === 0 ? $event.options[0]?.value : undefined;

    this.manageCanSelectChildren();
    this.rebuildContainerMap();

    const selection: ISelectedHierarchyInfo = this.getSelectionInfo(
      this.selectedHierarchyContainerItems,
      lastSelectedOption
    );
    this.onHierarchyContainerItemsChanged.emit(selection);
  }

  hierarchyFacetItemsChanged(_$event: MatSelectionListChange): void {
    const selection: ISelectedHierarchyInfo = this.getSelectionInfo(this.selectedHierarchyFacetItems);
    this.onHierarchyItemsChanged.emit(selection);
  }

  private buildHierarchyContainerItemsList(): void {
    this.hierarchyContainerItemsList = this.item.items?.filter((hi) => TypeCheck.isHierarchyContainer(hi.type)) ?? [];
  }

  private buildHierarchyFacetItemsList(): void {
    this.hierarchyFacetItemsList = this.item.items?.filter((hi) => TypeCheck.isFacet(hi.type)) ?? [];
  }

  private setSelectionMode(): void {
    this.canSelectAll = this.hierarchyFacetItemsList.length > 0;
  }

  private mapValues(items: IFacetedFilterDataItem[]): IHierarchyValue[] {
    const values: IHierarchyValue[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item) {
        values.push(item.value as IHierarchyValue);
      }
    }
    return values;
  }

  private manageCanSelectChildren(): void {
    if (this.selectedHierarchyContainerItems.length > 1) {
      this.canSelectChildren = false;
      this.selectedHierarchyFacetItems = [];
    } else {
      this.canSelectChildren = true;
    }
  }

  private getPurgePath(lastOption?: IFacetedFilterDataItem): string | undefined {
    if (!this.selectedHierarchyContainerItems.length || this.selectedHierarchyContainerItems.length > 1) {
      if (lastOption) return lastOption.path;

      return this.selectedHierarchyContainerItems[0]?.path;
    }
    return undefined;
  }

  private getSelectionInfo(
    selectedItems: IFacetedFilterDataItem[],
    lastSelectedOption?: IFacetedFilterDataItem
  ): ISelectedHierarchyInfo {
    const selection: ISelectedHierarchyInfo = {
      hierarchies: selectedItems?.length ? this.mapValues(selectedItems) : [],
      item: this.getSelectionItem(selectedItems),
      purgePath: this.getPurgePath(lastSelectedOption),
    };

    return selection;
  }

  private getSelectionItem(selectedItems: IFacetedFilterDataItem[]): IFacetedFilterDataItem {
    if (!selectedItems?.length || selectedItems.length > 1) return this.item;
    else return selectedItems[0] ?? this.item;
  }

  private scrollToElement(el: Element): void {
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 350);
  }

  private handleReplayItem(replayItem: IReplayItem): void {
    setTimeout(() => {
      const selected = this.item.items?.find((i) => i.label === replayItem.value);
      const idx: number = this.item.items?.findIndex((i) => i.label === replayItem.value) ?? -1;
      let idxPrefix: string;
      if (selected) {
        let selection: ISelectedHierarchyInfo;
        if (TypeCheck.isHierarchyContainer(selected.type)) {
          this.selectedHierarchyContainerItems = [selected];
          this.rebuildContainerMap();
          selection = this.getSelectionInfo(this.selectedHierarchyContainerItems);
          idxPrefix = 'c_';
        } else {
          this.selectedHierarchyFacetItems = [selected];
          this.updateCheckboxState();
          selection = this.getSelectionInfo(this.selectedHierarchyFacetItems);
          idxPrefix = 'f_';
        }

        this.onHierarchyReplaySelection.emit(selection);
        const firstSelectedEl = document.getElementById(`${idxPrefix}${idx}`);
        if (firstSelectedEl) {
          this.scrollToElement(firstSelectedEl);
        }
      } else {
        console.debug('>>>>>>>>HANDLE REPLAY ITEM NOT SELECTED<<<<<<<<');
      }
    }, 1);
  }
}
